import { compile } from 'path-to-regexp';

import config from 'configs/app';

import isNeedProxy from './isNeedProxy';
import { RESOURCES } from './resources';
import type { ApiResource, ResourceName, ResourcePathParams } from './resources';

export default function buildUrl<R extends ResourceName>(
  resourceName: R,
  pathParams?: ResourcePathParams<R>,
  queryParams?: Record<string, string | Array<string> | number | boolean | null | undefined>,
  noProxy?: boolean,
): string {
  const resource: ApiResource = RESOURCES[resourceName];
  const useAppInternalTxsProxy = resourceName === 'internal_txs';
  const useAppStatsProxy = resourceName === 'stats';
  const useAppTokenProxy = resourceName === 'token';
  const useAppTokenCountersProxy = resourceName === 'token_counters';
  const useAppTokenHoldersProxy = resourceName === 'token_holders';
  const useAppTokenTransfersProxy = resourceName === 'token_transfers';
  const useAppAddressTokenTransfersProxy = resourceName === 'address_token_transfers';
  const useAppAddressInternalTxsProxy = resourceName === 'address_internal_txs';
  const useAppProxy = useAppInternalTxsProxy || useAppStatsProxy || useAppTokenProxy || useAppTokenCountersProxy || useAppTokenHoldersProxy || useAppTokenTransfersProxy || useAppAddressTokenTransfersProxy || useAppAddressInternalTxsProxy;
  let baseUrl: string;
  if (useAppProxy) {
    if (typeof window !== 'undefined') {
      baseUrl = window.location.origin;
    } else {
      const appBase = config.app.baseUrl;
      const hasValidHost = appBase && appBase.length > 10 && appBase.includes('://') && !appBase.endsWith('://');
      if (hasValidHost) {
        baseUrl = appBase;
      } else {
        const port = typeof process !== 'undefined' && process.env?.PORT ? process.env.PORT : '3000';
        baseUrl = `http://127.0.0.1:${port}`;
      }
    }
  } else {
    if (!noProxy && isNeedProxy()) {
      baseUrl = typeof window !== 'undefined' ? window.location.origin : (config.app.baseUrl || config.api.endpoint);
    } else {
      baseUrl = resource.endpoint || config.api.endpoint;
    }
  }
  const basePath = useAppProxy ? '' : (resource.basePath !== undefined ? resource.basePath : config.api.basePath);
  let path: string;
  if (useAppInternalTxsProxy) {
    path = '/api/internal-transactions';
  } else if (useAppStatsProxy) {
    path = '/api/stats';
  } else if (useAppTokenProxy) {
    path = '/api/proxy/token/' + encodeURIComponent(pathParams?.hash ?? '');
  } else if (useAppTokenCountersProxy) {
    path = '/api/proxy/token/' + encodeURIComponent(pathParams?.hash ?? '') + '/counters';
  } else if (useAppTokenHoldersProxy) {
    path = '/api/proxy/token/' + encodeURIComponent(pathParams?.hash ?? '') + '/holders';
  } else if (useAppTokenTransfersProxy) {
    path = '/api/proxy/token/' + encodeURIComponent(pathParams?.hash ?? '') + '/transfers';
  } else if (useAppAddressTokenTransfersProxy) {
    path = '/api/proxy/address/' + encodeURIComponent(pathParams?.hash ?? '') + '/token-transfers';
  } else if (useAppAddressInternalTxsProxy) {
    path = '/api/proxy/address/' + encodeURIComponent(pathParams?.hash ?? '') + '/internal-transactions';
  } else {
    const fullPath = basePath + resource.path;
    const compiledPath = (pathParams && Object.keys(pathParams).length > 0)
      ? compile(fullPath)(pathParams as Record<string, string>)
      : fullPath;
    path = (!noProxy && isNeedProxy() ? '/node-api/proxy' + compiledPath : compiledPath);
  }
  const url = new URL(path, baseUrl);

  queryParams && Object.entries(queryParams).forEach(([ key, value ]) => {
    // there are some pagination params that can be null or false for the next page
    value !== undefined && value !== '' && url.searchParams.append(key, String(value));
  });

  return url.toString();
}
